import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

/**
 * Global provider that fetches every published image override from
 * /api/images once at app-start and exposes a resolver so any component
 * can render the CMS-managed URL for a given slot key.
 *
 * Enriched to also expose per-slot slideshow config (up to 4 slides +
 * rotation interval) so `CmsImageSlideshow` can auto-rotate frames.
 *
 * Consumers:
 *   <CmsImage slotKey="home.hero" defaultSrc="/xega-banner.jpg" alt="..." />
 *   <CmsImageSlideshow slotKey="home.hero" defaultSrc="/xega-banner.jpg" />
 *   const { resolve, resolveSlot } = useImages();
 */
const ImagesCtx = createContext({
  map: {},
  slots: {},
  ready: false,
  resolve: (_k) => null,
  resolveSlot: (_k) => ({ slides: [], interval_ms: 5000 }),
  reload: () => {},
});

export function ImagesProvider({ children }) {
  const [map, setMap] = useState({});
  const [slotMap, setSlotMap] = useState({});
  const [ready, setReady] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/images");
      setMap(r.data?.images || {});
      setSlotMap(r.data?.slots || {});
    } catch (e) {
      // swallow — CmsImage will still render its defaultSrc
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const resolve = useCallback((key) => (key ? map[key] : null), [map]);
  const resolveSlot = useCallback(
    (key) => (key ? slotMap[key] || null : null),
    [slotMap]
  );

  return (
    <ImagesCtx.Provider value={{ map, slots: slotMap, ready, resolve, resolveSlot, reload }}>
      {children}
    </ImagesCtx.Provider>
  );
}

export function useImages() {
  return useContext(ImagesCtx);
}

/**
 * Drop-in replacement for <img>. If the slotKey has a CMS override,
 * that URL is used; otherwise the defaultSrc is served.
 *
 * Every other prop is forwarded to the underlying <img> tag so callers
 * can keep their existing classes / loading strategies untouched.
 */
export function CmsImage({ slotKey, defaultSrc, alt = "", ...rest }) {
  const { resolve, ready } = useImages();
  const src = (ready && resolve(slotKey)) || defaultSrc;
  return <img src={src} alt={alt} {...rest} />;
}


const VIDEO_EXTS = /\.(mp4|webm|ogv|mov|m4v)(\?.*)?$/i;

function isVideoUrl(url) {
  if (!url) return false;
  if (VIDEO_EXTS.test(url)) return true;
  // Cloudinary video URLs always contain /video/upload/
  if (/\/video\/upload\//i.test(url)) return true;
  return false;
}

/**
 * Slot that supports EITHER image or video. If the CMS-published URL is a
 * video, we render a muted, autoplaying, looping <video> element (perfect
 * for background animations). Otherwise, an <img>. When there is no CMS
 * override and no defaultSrc, the `fallback` React node is rendered — this
 * lets us keep purely-code animations (SVG, canvas) as the default while
 * still allowing the admin to override with a static asset later.
 */
export function CmsMedia({ slotKey, defaultSrc, fallback = null, className = "", alt = "", videoProps = {}, ...rest }) {
  const { resolve, ready } = useImages();
  const url = (ready && resolve(slotKey)) || defaultSrc || "";

  if (!url) return fallback;

  if (isVideoUrl(url)) {
    return (
      <video
        src={url}
        className={className}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        {...videoProps}
      />
    );
  }
  return <img src={url} alt={alt} className={className} {...rest} />;
}


/**
 * Cinematic slideshow — up to 4 images rotating with a Ken Burns crossfade.
 * If the slot has 0 or 1 extra slides, this behaves like `CmsImage` (single
 * static frame). Otherwise, the frames auto-advance every `interval_ms` (set
 * per slot in the admin) with a soft 900 ms crossfade and slow zoom.
 *
 * Preserves the API of `CmsImage` — same className / object-fit / etc.
 */
export function CmsImageSlideshow({ slotKey, defaultSrc, alt = "", className = "", ...rest }) {
  const { resolveSlot, ready } = useImages();
  const slot = ready ? resolveSlot(slotKey) : null;

  // Build the final ordered list of slide URLs (primary first, then extras)
  const slides = React.useMemo(() => {
    const arr = [];
    if (slot?.slides?.length) {
      for (const s of slot.slides) {
        if (s?.url) arr.push(s.url);
      }
    } else if (slot?.url) {
      arr.push(slot.url);
    } else if (defaultSrc) {
      arr.push(defaultSrc);
    }
    // Deduplicate consecutive dupes
    return arr.filter((u, i) => u && u !== arr[i - 1]);
  }, [slot, defaultSrc]);

  const intervalMs = slot?.interval_ms || 5000;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const id = setInterval(() => {
      setIdx((prev) => (prev + 1) % slides.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [slides.length, intervalMs]);

  // Single-image mode — render a plain <img> to keep it lightweight
  if (slides.length <= 1) {
    const src = slides[0] || defaultSrc;
    return <img src={src} alt={alt} className={className} {...rest} />;
  }

  return (
    <div className={`relative overflow-hidden ${className}`} data-testid={`cms-slideshow-${slotKey}`}>
      <style>{`
        @keyframes cms-kenburns {
          0%   { transform: scale(1.02) translate(0, 0); }
          100% { transform: scale(1.14) translate(-1.5%, 1.5%); }
        }
        @keyframes cms-kenburns-alt {
          0%   { transform: scale(1.14) translate(1.5%, -1.5%); }
          100% { transform: scale(1.02) translate(0, 0); }
        }
      `}</style>
      {slides.map((url, i) => {
        const active = i === idx;
        const isVideo = isVideoUrl(url);
        const kb = i % 2 === 0 ? 'cms-kenburns' : 'cms-kenburns-alt';
        const commonStyle = {
          transition: 'opacity 900ms ease-in-out',
          opacity: active ? 1 : 0,
          zIndex: active ? 2 : 1,
        };
        const innerStyle = active
          ? { animation: `${kb} ${(intervalMs + 1200) / 1000}s ease-out both`, willChange: 'transform' }
          : { transform: 'scale(1.02)' };
        return (
          <div key={`${url}-${i}`} className="absolute inset-0" style={commonStyle} aria-hidden={!active}>
            {isVideo ? (
              <video
                src={url}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="absolute inset-0 w-full h-full object-cover"
                style={innerStyle}
              />
            ) : (
              <img
                src={url}
                alt={i === 0 ? alt : ""}
                className="absolute inset-0 w-full h-full object-cover"
                style={innerStyle}
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
